import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchSentimentPosts, Article } from "../../api";
import { SOURCE_BADGE, SENT_META, ISSUE_TYPE_META, EMOTION_META, fmtCount } from "./shared";

interface Props {
  product: string;
  dateFrom?: string;
  dateTo?: string;
}

function PostItem({ article }: { article: Article }) {
  const dateStr = article.published_at ?? article.crawled_at;
  const date = dateStr ? dateStr.slice(0, 10) : undefined;
  const src = SOURCE_BADGE[article.source_code ?? ""] ?? { label: "帖", cls: "bg-ink-faint text-white" };
  const sent = SENT_META[article.sentiment ?? "neutral"] ?? SENT_META.neutral;
  const issueMeta = article.issue_type ? ISSUE_TYPE_META[article.issue_type] : null;
  const emotionMeta = article.emotion_intensity ? EMOTION_META[article.emotion_intensity] : null;
  const meta = article.metadata_ || {};
  const likes = fmtCount(meta.likes);
  const comments = fmtCount(meta.comments);
  const shares = fmtCount(meta.shares);
  const hasEngagement = !!(likes || comments || shares);

  return (
    <div className="border-b border-border/80 py-3 last:border-0">
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold ${src.cls}`}>
          {src.label}
        </span>
        <div className="min-w-0 flex-1">
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block line-clamp-2 text-[13px] leading-snug text-ink transition-colors hover:text-primary-600 dark:hover:text-primary-400"
          >
            {article.title}
          </a>
          {article.summary && article.summary !== article.title && <p className="mt-1 text-[12px] leading-relaxed text-ink-secondary line-clamp-2">{article.summary}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {date && <span className="text-[11px] font-mono tabular-nums text-ink-faint">{date}</span>}
            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${sent.badge}`}>
              <span className={`h-1 w-1 rounded-full ${sent.dot}`} />
              {sent.label}
            </span>
            {issueMeta && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${issueMeta.cls}`}>{issueMeta.label}</span>}
            {emotionMeta && emotionMeta.label !== "平和" && <span className={`text-[10px] ${emotionMeta.cls}`}>{emotionMeta.label}</span>}
            {hasEngagement && (
              <span className="ml-auto flex items-center gap-1.5 text-[10px] text-ink-faint">
                {likes && <span title="点赞">赞 {likes}</span>}
                {comments && <span title="评论">评 {comments}</span>}
                {shares && <span title="转发/收藏">转 {shares}</span>}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PostsPanel({ product, dateFrom, dateTo }: Props) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["sentimentPosts", product, dateFrom ?? "", dateTo ?? ""],
    queryFn: ({ pageParam = 1 }) => fetchSentimentPosts(product, pageParam as number, dateFrom, dateTo),
    initialPageParam: 1,
    getNextPageParam: (last) => last.has_more ? last.page + 1 : undefined,
    staleTime: 2 * 60 * 1000,
  });

  // Deduplicate by article ID — useInfiniteQuery can produce overlapping pages
  // when queryKey params change mid-flight (e.g. date picker / tab switch).
  const rawPosts = data?.pages.flatMap(p => p.items) ?? [];
  const posts = useMemo(() => {
    const seen = new Set<number>();
    return rawPosts.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [rawPosts]);

  if (isLoading) return (
    <div className="space-y-2.5">
      {[1,2,3,4].map(i => <div key={i} className="h-14 bg-surface rounded-xl animate-pulse" />)}
    </div>
  );
  if (!posts.length) return (
    <div className="py-10 text-center text-[13px] text-ink-faint">暂无帖子数据</div>
  );

  return (
    <div>
      {posts.map(a => <PostItem key={a.id} article={a} />)}
      {hasNextPage && (
        <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
          className="w-full py-2.5 text-[12px] text-primary-500 hover:text-primary-700 disabled:text-ink-faint mt-1 transition-colors">
          {isFetchingNextPage ? "加载中…" : "加载更多"}
        </button>
      )}
    </div>
  );
}
